import { useMeStore } from '@/services/UserService/useMeStore';

/**
 * Company Context Service
 *
 * Manages company context to prevent stale UI state from writing to wrong accounts
 * when users switch firms in different browser tabs.
 *
 * Key responsibilities:
 * - Initialize and store company ID at page load
 * - Provide company ID for request headers
 * - Detect context mismatches between UI and auth
 */

export interface CompanyContext {
  companyId: string;
  companyName: string;
  loadedAt: number;
}

export class CompanyContextService {
  private static instance: CompanyContextService;
  private pageLoadCompanyId: string | null = null;
  private pageLoadCompanyName: string | null = null;
  private pageLoadedAt: number | null = null;
  private initialized = false;

  public static getInstance(): CompanyContextService {
    if (!CompanyContextService.instance) {
      CompanyContextService.instance = new CompanyContextService();
    }
    return CompanyContextService.instance;
  }

  /**
   * Initialize company context at page load
   * Should be called once during application bootstrap
   * Stores the company context in memory (NOT localStorage to avoid cross-tab conflicts)
   */
  public async initializeContext(): Promise<void> {
    if (this.initialized) {
      return;
    }

    try {
      const meStore = useMeStore();

      // Force refresh to get current auth context
      const me = await meStore.getMe(true);

      if (me && me.company && me.company.id) {
        // Store page-specific context in memory only (NOT localStorage)
        // This ensures each tab/page maintains its own context
        this.pageLoadCompanyId = me.company.id;
        this.pageLoadCompanyName = me.company.name || 'Unknown';
        this.pageLoadedAt = Date.now();
        this.initialized = true;

        console.log(
          '[CompanyContextService] Initialized page-specific context',
          {
            companyId: me.company.id,
            companyName: me.company.name,
          },
        );
      } else {
        console.warn(
          '[CompanyContextService] Unable to initialize - no company data in me object',
        );
      }
    } catch (error) {
      console.error(
        '[CompanyContextService] Failed to initialize context:',
        error,
      );
      // Don't throw - app should continue to function
    }
  }

  /**
   * Get the company ID that was active when the page was loaded
   * This is used in request headers to detect context mismatches
   */
  public getPageLoadCompanyId(): string | null {
    return this.pageLoadCompanyId;
  }

  /**
   * Get full company context information (page-specific context)
   * Returns the context that was captured when this page loaded,
   * NOT the current localStorage context (which may be from another tab)
   */
  public getContext(): CompanyContext | null {
    if (
      this.initialized &&
      this.pageLoadCompanyId &&
      this.pageLoadCompanyName
    ) {
      return {
        companyId: this.pageLoadCompanyId,
        companyName: this.pageLoadCompanyName,
        loadedAt: this.pageLoadedAt!,
      };
    }
    return null;
  }

  /**
   * Clear stored context (used on logout or manual reset)
   */
  public clearContext(): void {
    this.pageLoadCompanyId = null;
    this.pageLoadCompanyName = null;
    this.pageLoadedAt = null;
    this.initialized = false;
  }

  /**
   * Check if context has been initialized
   */
  public isInitialized(): boolean {
    return this.initialized && !!this.pageLoadCompanyId;
  }

  /**
   * Manually set context (for testing or special cases)
   * Sets page-specific context in memory only
   */
  public setContext(companyId: string, companyName: string): void {
    this.pageLoadCompanyId = companyId;
    this.pageLoadCompanyName = companyName;
    this.initialized = true;

    console.log('[CompanyContextService] Context manually set', {
      companyId,
      companyName,
    });
  }
}

// Export singleton instance for easy access
export const companyContextService = CompanyContextService.getInstance();
